import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Services from "./components/Services";
import About from "./components/About";
import Testimonials from "./components/Testimonials";
import CaseStudies from "./components/CaseStudies";
import Investments from "./components/Investments";
import FAQ from "./components/FAQ";
import Blog from "./components/Blog";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import LoginModal from "./components/LoginModal";
import AdminPanel from "./components/AdminPanel";
import WorldClockTicker from "./components/WorldClockTicker";
import CustomPanel from "./components/CustomPanel";
import SEO from "./components/SEO";
import AstroPowerSystems from "./components/AstroPowerSystems";
import usePageviewTracker from "./hooks/usePageviewTracker";
import UrlsForSale from "./components/UrlsForSale";
import RealEstateForSale from "./components/RealEstateForSale";
import EMuscleCar from "./components/EMuscleCar";
import EMuscleCarComponents from "./components/EMuscleCarComponents";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Panel components mapping
const panelComponents = {
  hero: Hero,
  services: Services,
  about: About,
  testimonials: Testimonials,
  "case-studies": CaseStudies,
  investments: Investments,
  faq: FAQ,
  insights: Blog,
  contact: Contact,
};

// Default panel order
const defaultPanels = [
  { id: "hero", order: 0, visible: true },
  { id: "services", order: 1, visible: true },
  { id: "about", order: 2, visible: true },
  { id: "testimonials", order: 3, visible: true },
  { id: "case-studies", order: 4, visible: true },
  { id: "investments", order: 5, visible: true },
  { id: "faq", order: 6, visible: true },
  { id: "insights", order: 7, visible: true },
  { id: "contact", order: 8, visible: true },
];

const MainContent = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  usePageviewTracker();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  // panels = null means "layout not loaded yet" — prevents FOUC where hidden
  // tabs/sections briefly flash in before the real layout is fetched.
  const [panels, setPanels] = useState(null);
  const [footerSettings, setFooterSettings] = useState(null);

  // Fetch layout and settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Fetch layout
        const layoutRes = await axios.get(`${API}/settings/layout`);
        if (layoutRes.data.panels && layoutRes.data.panels.length > 0) {
          setPanels(layoutRes.data.panels);
        } else {
          setPanels(defaultPanels);
        }

        // Fetch site settings (footer)
        const siteRes = await axios.get(`${API}/settings/site`);
        if (siteRes.data.footer) {
          setFooterSettings(siteRes.data.footer);
        }
      } catch (error) {
        console.log("Using default settings");
        setPanels(defaultPanels);
      }
    };

    fetchSettings();
  }, []);

  const handleLayoutChange = (newPanels) => {
    setPanels(newPanels);
  };

  const handleContentChange = () => {
    // Trigger re-fetch of content by incrementing a key
    setContentKey(prev => prev + 1);
  };

  const [contentKey, setContentKey] = useState(0);

  const location = useLocation();
  // Some routes are standalone landing pages (used to point external domains
  // at this app). Skip the Shire site chrome on those.
  const standaloneRoutes = ['/astropowersystems'];
  const isStandalone = standaloneRoutes.includes(location.pathname);

  // Sort panels by order and filter visible ones. When panels is null
  // (still loading), render nothing to avoid flashing hidden sections.
  const sortedPanels = panels
    ? [...panels].sort((a, b) => a.order - b.order).filter((panel) => panel.visible)
    : null;

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      {!isStandalone && <WorldClockTicker />}
      {!isStandalone && (
        <Header
          onLoginClick={() => setShowLoginModal(true)}
          onAdminClick={() => setShowAdminPanel(true)}
          panels={panels}
        />
      )}
      <Routes>
        <Route
          path="/"
          element={
            <main>
              <SEO
                title="Strategic Business Consulting & Financial Analysis"
                description="Private third-party business analysis, non-conventional financing, workouts, CRM/ERP implementation, and strategic consulting. 35+ years of experience helping 200+ small businesses succeed."
                path="/"
              />
              {sortedPanels?.map((panel) => {
                // Built-in panel
                const Component = panelComponents[panel.id];
                if (Component) {
                  return <Component key={`${panel.id}-${contentKey}`} />;
                }
                // Custom cloned panel
                if (panel.isCustom) {
                  return (
                    <CustomPanel
                      key={`${panel.id}-${contentKey}`}
                      panel={panel}
                    />
                  );
                }
                return null;
              })}
            </main>
          }
        />
        <Route
          path="/urlsforsale"
          element={
            <main>
              <UrlsForSale key={`urls-${contentKey}`} />
            </main>
          }
        />
        <Route
          path="/realestateforsale"
          element={
            <main>
              <RealEstateForSale key={`re-${contentKey}`} />
            </main>
          }
        />
        <Route path="/astropowersystems" element={<AstroPowerSystems />} />
        <Route
          path="/e-musclecars"
          element={
            <main>
              <EMuscleCar />
            </main>
          }
        />
        <Route
          path="/e-musclecars/components-consulting"
          element={
            <main>
              <EMuscleCarComponents />
            </main>
          }
        />
        {/* Legacy singular paths — redirect for any inbound links from the
            older URLs so nothing 404s. */}
        <Route path="/e-musclecar" element={<Navigate to="/e-musclecars" replace />} />
        <Route
          path="/e-musclecar/components-consulting"
          element={<Navigate to="/e-musclecars/components-consulting" replace />}
        />
      </Routes>
      {!isStandalone && <Footer settings={footerSettings} />}

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />

      {/* Admin Panel */}
      {isAuthenticated && isAdmin() && (
        <AdminPanel
          isOpen={showAdminPanel}
          onClose={() => setShowAdminPanel(false)}
          onLayoutChange={handleLayoutChange}
          onContentChange={handleContentChange}
        />
      )}
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <MainContent />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
